import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { getConsent, setConsent, onCookieSettingsOpen } from '@/lib/consent';
import { GRANTED, DENIED, isPrivatePath } from '@/lib/privacyRules';

// Staff consoles get no banner. With no answer nothing is loaded there either.
const HIDDEN_PATHS = ['/admin', '/carer-admin', '/cs', '/pm'];

export function CookieConsent() {
  const { pathname } = useLocation();
  const [choice, setChoice] = useState(getConsent);
  const [reopened, setReopened] = useState(false);

  // A "Cookie settings" link anywhere in the app opens the banner again.
  useEffect(() => onCookieSettingsOpen(() => {
    setChoice(getConsent());
    setReopened(true);
  }), []);

  // lib/analytics listens for the change: "granted" loads Google Analytics,
  // "denied" stops it and removes its cookies.
  const decide = (value) => {
    setConsent(value);
    setChoice(value);
    setReopened(false);
  };

  // Shared Health Passport pages never run analytics, so there is nothing to ask.
  const hidden = isPrivatePath(pathname) || HIDDEN_PATHS.some(p => pathname.startsWith(p));
  if (!reopened && (choice || hidden)) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[9999] p-4" role="region" aria-label="Cookie consent" data-testid="cookie-consent-banner">
      <div className="relative max-w-3xl mx-auto rounded-2xl shadow-2xl border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        style={{ background: 'var(--surface)', borderColor: '#E2E8F0' }}>
        <div className={`flex-1${choice ? ' pr-6 sm:pr-0' : ''}`}>
          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-main)' }}>We use cookies to improve your experience.</p>
          <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>
            We use Google Analytics to understand how people use nsofar so we can make it better. Your health data is never shared with Google.{' '}
            <a href="/cookies" className="underline" style={{ color: 'var(--color-insurance)' }}>Read our Cookie Policy</a>
          </p>
          {choice && (
            <p className="text-xs font-semibold mt-2" style={{ color: 'var(--text-main)' }} data-testid="cookie-current-choice">
              Your current choice: {choice === GRANTED ? 'Accepted' : 'Declined'}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
          <button onClick={() => decide(DENIED)}
            className="flex-1 sm:flex-initial px-5 py-2 rounded-full text-xs font-semibold border transition-colors hover:bg-[var(--hover-surface)]"
            style={{ color: '#64748B', borderColor: '#E2E8F0' }}
            data-testid="cookie-decline-btn">
            Decline
          </button>
          <button onClick={() => decide(GRANTED)}
            className="flex-1 sm:flex-initial px-5 py-2 rounded-full text-xs font-semibold text-white transition-all hover:shadow-md"
            style={{ background: 'var(--color-insurance)' }}
            data-testid="cookie-accept-btn">
            Accept
          </button>
        </div>
        {/* Reopened from a "Cookie settings" link with a choice already stored:
            closing keeps that choice. A first-time visitor gets no close button. */}
        {choice && (
          <button onClick={() => setReopened(false)} aria-label="Close cookie settings"
            className="absolute top-1 right-1 flex items-center justify-center w-8 rounded-full hover:bg-[var(--hover-surface)]"
            style={{ color: '#64748B' }}
            data-testid="cookie-close-btn">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
